import {create} from 'zustand';
import {ApiClient} from '@/api/ApiClient';
import {TransAccount} from "@/common/dto/TransAccount";
import {AccountData} from "@/common/req/AccountData";
import {CloseAccountReq} from "@/common/req/CloseAccountReq";

interface AccountStore {
    accounts: TransAccount[];
    isLoading: boolean;
    error: string | null;
    clearAccounts: () => void;
    loadAccounts: () => Promise<void>;
    createAccount: (data: AccountData) => Promise<TransAccount>;
    updateAccount: (id: number, data: AccountData) => Promise<void>;
    closeAccount: (id: number, data: CloseAccountReq) => Promise<void>;
    getAccountById: (id: number) => TransAccount | undefined;
    getAccountNameById: (id: number) => string;
}

export const useAccountStore = create<AccountStore>((set, get) => ({
    accounts: [],
    isLoading: true,
    error: null,

    clearAccounts: () => {
        set({
            accounts: [],
            isLoading: false,
            error: null
        });
    },

    loadAccounts: async () => {
        set({isLoading: true, error: null});
        try {
            const accounts = await ApiClient.instance.getAccounts();
            accounts.sort((a, b) => a.name.localeCompare(b.name));
            set({accounts, isLoading: false});
        } catch (error) {
            set({
                error: error instanceof Error ? error.message : 'Failed to load accounts',
                isLoading: false
            });
        }
    },

    createAccount: async (data: AccountData) => {
        const newAccount = await ApiClient.instance.createAccount(data);
        get().loadAccounts();
        return newAccount;
    },

    updateAccount: async (id: number, data: AccountData) => {
        await ApiClient.instance.updateAccount(id, data);
        get().loadAccounts();
    },

    closeAccount: async (id: number, data: CloseAccountReq) => {
        await ApiClient.instance.closeAccount(id, data);
        get().loadAccounts();
    },

    getAccountById: (id: number) => {
        return get().accounts.find(account => account.id === id);
    },

    getAccountNameById: (id: number) => {
        return get().accounts.find(account => account.id === id)?.name || id.toString();
    }
})); 